import React, { useState, useEffect, useCallback } from 'react'
import {
  CCard, CCardBody, CCardHeader, CButton, CCol, CRow,
  CFormGroup, CLabel, CInput,
} from 'compat/coreui'
import { toast } from 'react-toastify';
import { postData, getData } from '../../../helpers/api';

const API = process.env.REACT_APP_APP_API_URL;

const CATEGORIES = [
  ['electronics', 'Electronics'], ['fashion', 'Fashion'], ['grocery', 'Grocery'],
  ['home & living', 'Home & Living'], ['beauty & health', 'Beauty & Health'],
  ['sports & outdoors', 'Sports & Outdoors'], ['books & media', 'Books & Media'],
  ['services', 'Services'], ['delivery service', 'Delivery Service'], ['other', 'Other'],
];

const EMPTY = {
  businessName: '', businessType: '', businessEmail: '', phone: '', webUrl: '',
  walletAddress: '', address: '', address2: '', city: '', state: '', pincode: '',
  about: '', note: '',
  firstName: '', lastName: '', alternateEmail: '', alternatePhone: '',
};

export default function EditProfile() {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const load = useCallback(() => {
    getData(`${API}/api/user`).then(res => {
      if (!res.data.status) return;
      const u = res.data.user || {};
      setForm({
        businessName: u.businessName || '',
        businessType: u.businessType || '',
        businessEmail: u.businessEmail || '',
        phone: u.ownerPhone || '',
        webUrl: u.website || '',
        walletAddress: u.walletAddress || '',
        address: u.address1 || '',
        address2: u.address2 || '',
        city: u.city || '',
        state: u.state || '',
        pincode: u.pincode || '',
        about: (u.about && u.about !== 'about testing') ? u.about : '',
        note: (u.note && u.note !== 'note testing') ? u.note : '',
        firstName: u.manager?.firstName || '',
        lastName: u.manager?.lastName || '',
        alternateEmail: u.manager?.alternateEmail || '',
        alternatePhone: u.manager?.alternatePhone || '',
      });
    }).catch(() => {});
  }, []);

  useEffect(() => { load() }, [load]);

  const save = (e) => {
    e.preventDefault();
    if (!form.businessName || !form.phone || !form.address || !form.city) {
      toast.warn('Business name, phone, address, and city are required');
      return;
    }
    if (form.walletAddress && !/^0x[0-9a-fA-F]{40}$/.test(form.walletAddress)) {
      toast.warn('The wallet address must be a valid 0x… address');
      return;
    }
    setSaving(true);
    postData(`${API}/api/editprofile`, {
      businessName: form.businessName,
      businessEmail: form.businessEmail,
      phone: form.phone,
      webUrl: form.webUrl,
      walletAddress: form.walletAddress,
      businessType: form.businessType,
      address: form.address,
      address2: form.address2,
      city: form.city,
      state: form.state,
      pincode: form.pincode,
      about: form.about,
      note: form.note,
      manager: {
        firstName: form.firstName,
        lastName: form.lastName,
        alternateEmail: form.alternateEmail,
        alternatePhone: form.alternatePhone,
      },
    }).then(res => {
      if (res.data.status === 200) toast.success('Profile saved');
      else toast.error(res.data.message || 'Could not save the profile');
    }).catch(() => toast.error('Could not save the profile')).finally(() => setSaving(false));
  };

  const field = (label, key, props = {}) => (
    <CCol lg={props.wide ? 12 : 6} key={key}>
      <CFormGroup>
        <CLabel>{label}{props.required && <span className="required">*</span>}</CLabel>
        <CInput type={props.type || 'text'} value={form[key]} onChange={set(key)} placeholder={props.placeholder || ''} style={props.mono ? { fontFamily: 'monospace' } : undefined} />
      </CFormGroup>
    </CCol>
  );

  return (
    <form onSubmit={save}>
      <CRow className="mb-3">
        <CCol>
          <h2 style={{ fontWeight: 800, marginBottom: 4 }}>Edit Profile</h2>
          <p className="text-muted">What shoppers see on your storefront — and where your BRC lands.</p>
        </CCol>
        <CCol style={{ textAlign: 'right' }}>
          <CButton color="success" className="text-white" type="submit" disabled={saving} style={{ padding: '12px 30px' }}>
            {saving ? 'Saving…' : 'Save Profile'}
          </CButton>
        </CCol>
      </CRow>

      <CRow>
        <CCol xl={6}>
          <CCard className="mb-4">
            <CCardHeader>Business Information</CCardHeader>
            <CCardBody>
              <CRow>
                {field('Business Name', 'businessName', { required: true })}
                <CCol lg={6}>
                  <CFormGroup>
                    <CLabel>Category<span className="required">*</span></CLabel>
                    <select className="custom-select" value={form.businessType} onChange={set('businessType')}>
                      <option value="">Select…</option>
                      {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </CFormGroup>
                </CCol>
                {field('Phone', 'phone', { required: true, placeholder: '+251 …' })}
                {field('Business Email', 'businessEmail', { type: 'email' })}
                {field('Website', 'webUrl', { wide: true, placeholder: 'https://…' })}
              </CRow>
            </CCardBody>
          </CCard>

          <CCard className="mb-4">
            <CCardHeader>⛓ On-Chain Payouts</CCardHeader>
            <CCardBody>
              <CFormGroup>
                <CLabel>BRC Wallet Address</CLabel>
                <CInput value={form.walletAddress} onChange={set('walletAddress')} placeholder="0x…" style={{ fontFamily: 'monospace' }} />
                <small className="text-muted">Single-shop orders paid in BRC are sent straight to this BRDPoSChain address.</small>
              </CFormGroup>
            </CCardBody>
          </CCard>

          <CCard className="mb-4">
            <CCardHeader>Manager <small className="text-muted">(private — never shown to shoppers)</small></CCardHeader>
            <CCardBody>
              <CRow>
                {field('First Name', 'firstName')}
                {field('Last Name', 'lastName')}
                {field('Alternate Email', 'alternateEmail', { type: 'email' })}
                {field('Alternate Phone', 'alternatePhone')}
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xl={6}>
          <CCard className="mb-4">
            <CCardHeader>Location</CCardHeader>
            <CCardBody>
              <CRow>
                {field('Street Address', 'address', { required: true, wide: true })}
                {field('Address 2', 'address2', { wide: true })}
                {field('City', 'city', { required: true })}
                {field('Region / State', 'state')}
                {field('Postal Code', 'pincode')}
              </CRow>
            </CCardBody>
          </CCard>

          <CCard className="mb-4">
            <CCardHeader>About Your Shop</CCardHeader>
            <CCardBody>
              <CFormGroup>
                <CLabel>About <small className="text-muted">(shown on your shop's About tab)</small></CLabel>
                <textarea className="form-control" rows={5} value={form.about} onChange={set('about')}
                  placeholder="Tell shoppers who you are, what you sell, and why they'll love it…" />
              </CFormGroup>
              <CFormGroup>
                <CLabel>Good to know <small className="text-muted">(pickup info, delivery areas, opening hours…)</small></CLabel>
                <textarea className="form-control" rows={3} value={form.note} onChange={set('note')}
                  placeholder="e.g. Open Mon–Sat 9am–7pm · Free delivery inside Addis Ababa" />
              </CFormGroup>
            </CCardBody>
          </CCard>

          <CCard className="mb-4">
            <CCardHeader>Photos</CCardHeader>
            <CCardBody>
              <p className="text-muted" style={{ marginBottom: 0 }}>
                Your logo, cover, and gallery photos are managed on the <strong>Photos</strong> page in the sidebar.
              </p>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </form>
  )
}
